"use client";

import { Check, ChevronDown, LoaderCircle, Search } from "lucide-react";
import { createPortal } from "react-dom";
import { forwardRef, type SelectHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useOnClickOutside } from "@/hooks/use-on-click-outside";
import { cn } from "@/lib/utils/cn";
import { useOverlayPosition } from "./use-overlay-position";

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;
export type ThemedSelectOption = { value: string; label: string; description?: string; disabled?: boolean };
export type ThemedSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: ThemedSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
  ariaLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
};

export function selectControlClassName(className?: string) {
  return cn("ui-control ui-select-trigger w-full cursor-pointer text-left aria-[invalid=true]:border-status-error aria-[invalid=true]:shadow-[0_0_0_3px_var(--semantic-error-surface)]", className);
}

export const themedSelectTriggerClass = "ui-select-trigger";
export const themedSelectMenuClass = "rounded-card border-card-border bg-surface-elevated p-ds-1 shadow-overlay";
export const toolbarSearchInputClass = "ui-control h-10 w-full min-w-[18rem]";
export const tableHeaderSearchInputClass = "ui-control h-9 min-h-9 w-[min(100%,14rem)] min-w-[9rem] shrink-0 pl-10 pr-3";
export const tableHeaderSelectTriggerClass = "h-9 min-h-9 border-border bg-surface-primary hover:border-accent/30";

export type TableHeaderSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  "aria-label"?: string;
};

export function TableHeaderSearch({ value, onChange, placeholder = "Search", className, inputClassName, "aria-label": ariaLabel }: TableHeaderSearchProps) {
  return (
    <div className={cn("ui-control flex h-9 min-h-9 w-[min(100%,14rem)] min-w-[9rem] shrink-0 items-center gap-2.5 px-3", className)}>
      <Search className="pointer-events-none h-4 w-4 shrink-0 text-muted" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className={cn("h-full w-full min-w-0 border-0 bg-transparent p-0 text-caption text-foreground outline-none placeholder:text-muted", inputClassName)}
      />
    </div>
  );
}

export function ThemedSelect({
  value,
  onChange,
  options,
  placeholder = "Select",
  disabled = false,
  invalid = false,
  loading = false,
  className,
  triggerClassName,
  menuClassName,
  optionClassName,
  ariaLabel,
  searchable = false,
  searchPlaceholder = "Search options",
}: ThemedSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const position = useOverlayPosition(open, rootRef, "bottom-start", "anchor");
  const selectedOption = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);
  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return !searchable || !normalized
      ? options
      : options.filter((option) => `${option.label} ${option.description ?? ""}`.toLowerCase().includes(normalized));
  }, [options, query, searchable]);
  const close = useCallback(() => setOpen(false), []);
  useOnClickOutside(rootRef, close, open, [menuRef]);

  useEffect(() => {
    if (disabled || loading) setOpen(false);
  }, [disabled, loading]);
  useEffect(() => {
    if (!open) setQuery("");
    else if (searchable) window.requestAnimationFrame(() => searchRef.current?.focus());
  }, [open, searchable]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        title={loading ? "Loading" : (selectedOption?.label ?? placeholder)}
        onClick={() => setOpen((current) => !current)}
        className={cn(selectControlClassName("inline-flex min-h-10 items-center gap-2 px-3 py-2 leading-[1.2] disabled:cursor-not-allowed disabled:opacity-55"), triggerClassName)}
      >
        <span className="min-w-0 flex-1 truncate">{loading ? "Loading…" : selectedOption?.label ?? placeholder}</span>
        {loading ? <LoaderCircle className="h-4 w-4 animate-spin text-muted" aria-hidden /> : <ChevronDown className={cn("h-4 w-4 text-muted transition-transform duration-fast", open && "rotate-180")} aria-hidden />}
      </button>
      {open && position && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              className={cn("overflow-hidden rounded-card border border-card-border bg-surface-elevated p-ds-1 shadow-overlay", menuClassName)}
              style={position}
              role="listbox"
              aria-label={ariaLabel}
            >
              {searchable ? (
                <div className="mb-ds-1 flex min-h-10 items-center gap-ds-2 rounded-control border border-card-border bg-surface-inset px-ds-3 py-ds-2">
                  <Search className="h-4 w-4 text-muted" aria-hidden />
                  <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} className="min-w-0 flex-1 border-0 bg-transparent text-caption text-foreground outline-none placeholder:text-muted" />
                </div>
              ) : null}
              <div className="max-h-72 overflow-y-auto">
                {filteredOptions.map((option) => {
                  const active = option.value === value;
                  return (
                    <button
                      key={option.value || "__empty"}
                      type="button"
                      role="option"
                      aria-selected={active}
                      disabled={option.disabled}
                      title={option.description ? `${option.label} — ${option.description}` : option.label}
                      onClick={() => {
                        if (!option.disabled) {
                          onChange(option.value);
                          close();
                        }
                      }}
                      className={cn(
                        "flex min-h-10 w-full items-center gap-ds-2 rounded-control px-ds-3 py-ds-2 text-left transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-45",
                        active ? "bg-accent-muted text-foreground" : "text-muted hover:bg-surface-hover hover:text-foreground",
                        optionClassName,
                      )}
                    >
                      <span className="min-w-0 flex-1"><span className="block truncate text-caption font-semibold" title={option.label}>{option.label}</span>{option.description ? <span className="mt-1 block truncate text-micro text-muted" title={option.description}>{option.description}</span> : null}</span>
                      {active ? <Check className="h-4 w-4 text-accent" aria-hidden /> : null}
                    </button>
                  );
                })}
                {filteredOptions.length === 0 ? <div className="px-ds-3 py-ds-6 text-center text-caption text-muted">No options found.</div> : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ className, ...props }, ref) {
  return <select ref={ref} className={selectControlClassName(className)} {...props} />;
});
